import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    GetAllPaymentOptInRequest,
    GetAllPaymentOptInResponse,
    GetAllPaymentOptInService,
    UpdateAllPaymentOptInRequest,
    UpdateAllPaymentOptInService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { LifecycleAware, Modal } from '@lsq-frontend/common-ui';
import { WorkflowMonitorService } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject } from 'rxjs';

@LifecycleAware()
@Component({
    selector: 'automatic-funding',
    template: `
        <div class="automatic-funding-container">
            <div class="section-header">
                <h2 class="section-heading h3" i18n="Automatic funding header">
                    Early Payment Options
                </h2>
            </div>

            <div class="automatic-terms-funding-contents" *ngIf="!isLoading">
                <div class="automatic-terms-funding-instructions" i18n>
                    Early payment request for eligible invoices can automatically be generated by
                    FastTrack on your behalf or through manual selection.
                </div>

                <div class="automatic-terms-funding-form-header" i18n>
                    Manage Early Payment Schedule
                </div>
                <div class="automatic-terms-funding-form-subtext" i18n>
                    How would you like to request early payments on your eligible invoices?
                </div>
                <form [formGroup]="automaticPaymentFormGroup">
                    <div>
                        <lsq-radio
                            i18n-label
                            label="Manual Selection"
                            [name]="'automaticEarlyPaymentSetting'"
                            [id]="'automatic-early-payment-disable'"
                            [value]="automaticEarlyPaymentSettings.DISABLED"
                            [group]="automaticPaymentFormGroup"
                            formControlName="automaticEarlyPaymentSetting"
                        >
                        </lsq-radio>
                        <div class="radio-subtext automatic-terms-funding-form-subtext" i18n>
                            You will need to individually select and request early payment on
                            eligible invoices manually.
                        </div>
                    </div>

                    <div>
                        <lsq-radio
                            i18n-label
                            label="Upon Approval"
                            [name]="'automaticEarlyPaymentSetting'"
                            [id]="'automatic-early-payment-immediately'"
                            [value]="automaticEarlyPaymentSettings.IMMEDIATELY"
                            [group]="automaticPaymentFormGroup"
                            formControlName="automaticEarlyPaymentSetting"
                        >
                        </lsq-radio>
                        <div class="radio-subtext automatic-terms-funding-form-subtext" i18n>
                            FastTrack automatically generates an early payment request immediately
                            when it receives an eligible invoice.
                        </div>
                    </div>

                    <div>
                        <lsq-radio
                            i18n-label
                            label="Set Your Own Terms"
                            [name]="'automaticEarlyPaymentSetting'"
                            [id]="'automatic-early-payment-terms'"
                            [value]="automaticEarlyPaymentSettings.TERMS"
                            [group]="automaticPaymentFormGroup"
                            formControlName="automaticEarlyPaymentSetting"
                        >
                        </lsq-radio>
                        <div class="radio-subtext automatic-terms-funding-form-subtext" i18n>
                            For eligible invoices, FastTrack will automatically schedule an early
                            payment request on a predefined number of days past your invoice date.
                            Invoice in this selection are still eligible for manual selection for
                            early payments.
                        </div>
                    </div>
                    <div class="automatic-terms-funding-form-terms-section">
                        <lsq-input
                            class="automatic-terms-funding-form-terms-input"
                            formControlName="termsDays"
                            [maxLength]="3"
                            [name]="'automatic-early-payment-terms-days'"
                            [group]="automaticPaymentFormGroup"
                            [digitsOnly]="true"
                            [floatLabel]="true"
                            [type]="'number'"
                        >
                        </lsq-input>
                        <span class="automatic-terms-funding-form-terms-label" i18n
                            >days from Invoice Date</span
                        >
                    </div>
                </form>
                <lsq-button
                    buttonClass="update-automatic-settings-btn"
                    (buttonClick)="openModal()"
                    [disabled]="saveButtonDisabled"
                    type="submit"
                    i18n
                >
                    Save
                </lsq-button>
            </div>

            <div *ngIf="isLoading">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
        </div>
    `,
    styleUrls: ['./automatic-funding.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AutomaticFundingComponent implements OnInit, OnDestroy {
    private _isModalSubmitting$ = new BehaviorSubject(false);
    public formGroup: FormGroup;
    public automaticPaymentFormGroup: FormGroup;
    public isLoading = true;
    public automaticEarlyPaymentSettings = AutomaticEarlyPaymentSettings;
    public persistedEarlyPaymentSettings: AutomaticEarlyPaymentSettings;
    public persistedEarlyPaymentTerms = null;
    public saveButtonDisabled = true;

    public onText = this._i18n({
        value: 'On'
    });

    public offText = this._i18n({
        value: 'Off'
    });

    public enableAutoPaymentsModalTitle = this._i18n({
        value: 'Enable Automatic Early Payments'
    });

    public enableAutoPaymentsModalText = this._i18n({
        value:
            'After turning on automatic early payments, you’ll start receiving early payments for eligible invoices as they are approved. Payments will be sent to your default, verified payment method on file.'
    });
    public enableAutoPaymentsOnTermsModalText = this._i18n({
        value:
            'Upon opting in, you will receive early payments for all eligible invoices based on invoice dates and the set terms. All scheduled payments will be available on the Payments page for reference soon. Payments are sent to the default verified payment method on file.'
    });

    public enableAutoPaymentsModalConfirm = this._i18n({
        value: 'Enable Automatic Early Payments'
    });

    public disableAutoPaymentsModalTitle = this._i18n({
        value: 'Disable Automatic Early Payments'
    });

    public disableAutoPaymentsModalText = this._i18n({
        value:
            'When Automatic Early Payments is turned off, you’ll need to manually select invoices on a request-by-request basis to receive early payment.'
    });

    public disableAutoPaymentsModalConfirm = this._i18n({
        value: 'Disable Automatic Early Payments'
    });

    public workflowWarning = this._i18n({
        value:
            'The changes to your Early Payment Options will not be saved if you navigate away from this page.'
    });

    public workflowNavigateAway = this._i18n({
        value: 'Navigate away'
    });

    public workflowKeepWorking = this._i18n({
        value: 'Keep working'
    });

    constructor(
        private _getAllPaymentOptInService: GetAllPaymentOptInService,
        private _updateAllPaymentOptInService: UpdateAllPaymentOptInService,
        private _userService: UserService,
        private _workflowMonitorService: WorkflowMonitorService,
        private _modalService: ModalService,
        private _i18n: I18n
    ) {}

    public ngOnInit(): void {
        const getOptInRequest = new GetAllPaymentOptInRequest({
            sellerKey: this._userService.getAlternateCompanyKey()
        });
        // tslint:disable-next-line: no-floating-promises
        this._getAllPaymentOptInService.executeOnce(getOptInRequest).then((response) => {
            const loadedSetting = this._getSetting(response);
            this.persistedEarlyPaymentSettings = loadedSetting;
            this.persistedEarlyPaymentTerms = response.terms;

            this.automaticPaymentFormGroup = new FormGroup({
                automaticEarlyPaymentSetting: new FormControl(loadedSetting),
                termsDays: new FormControl({
                    value: response.terms || null,
                    disabled: loadedSetting !== this.automaticEarlyPaymentSettings.TERMS
                })
            });

            this.automaticPaymentFormGroup
                .get('automaticEarlyPaymentSetting')
                .valueChanges.subscribe((val) => {
                    if (val === this.automaticEarlyPaymentSettings.TERMS) {
                        this.automaticPaymentFormGroup.get('termsDays').enable();
                        if (!!this.persistedEarlyPaymentTerms) {
                            this.automaticPaymentFormGroup
                                .get('termsDays')
                                .setValue(this.persistedEarlyPaymentTerms);
                        }
                    } else {
                        this.automaticPaymentFormGroup.get('termsDays').setValue(null);
                        this.automaticPaymentFormGroup.get('termsDays').disable();
                    }

                    this._workflowMonitorService.markWorkflowStarted(
                        this.workflowWarning,
                        this.workflowNavigateAway,
                        this.workflowKeepWorking
                    );
                });

            this.automaticPaymentFormGroup.valueChanges.subscribe((formValue) => {
                if (
                    formValue.automaticEarlyPaymentSetting !== this.persistedEarlyPaymentSettings ||
                    (!!formValue.termsDays &&
                        formValue.termsDays !== this.persistedEarlyPaymentTerms)
                ) {
                    this._workflowMonitorService.markWorkflowStarted(
                        this.workflowWarning,
                        this.workflowNavigateAway,
                        this.workflowKeepWorking
                    );
                    this.saveButtonDisabled = false;
                } else {
                    this._workflowMonitorService.markWorkflowFinished();
                    this.saveButtonDisabled = true;
                }
            });
            this.isLoading = false;
        });
    }

    public openModal(event?: Event): void {
        if (event) {
            // Older flow: This value will not change until we validate so it is the opposite of what the user is attempting to change it to.
            event.preventDefault();
        }
        if (
            this.automaticPaymentFormGroup.get('automaticEarlyPaymentSetting').value !==
            this.automaticEarlyPaymentSettings.DISABLED
        ) {
            this._isModalSubmitting$.next(false);
            this._modalService.showModal(
                new Modal({
                    id: 'confirm-enable-auto-early-payment',
                    title: this.enableAutoPaymentsModalTitle,
                    bodyText: this.enableAutoPaymentsOnTermsModalText,
                    showFooter: true,
                    showOkButton: true,
                    showCancelButton: true,
                    showCloseButton: true,
                    clickOutsideToClose: false,
                    okButtonText: this.enableAutoPaymentsModalConfirm,
                    isSubmitting$: this._isModalSubmitting$,
                    onOkClick: () => this._saveValue(),
                    onCloseButtonClick: () => this._revertChange(),
                    onCancelClick: () => this._revertChange()
                })
            );
        } else {
            this._modalService.showModal(
                new Modal({
                    id: 'confirm-disable-auto-early-payment',
                    title: this.disableAutoPaymentsModalTitle,
                    bodyText: this.disableAutoPaymentsModalText,
                    showFooter: true,
                    showOkButton: true,
                    showCancelButton: true,
                    showCloseButton: true,
                    clickOutsideToClose: false,
                    okButtonText: this.disableAutoPaymentsModalConfirm,
                    isSubmitting$: this._isModalSubmitting$,
                    onOkClick: () => this._saveValue(),
                    onCloseButtonClick: () => this._revertChange(),
                    onCancelClick: () => this._revertChange()
                })
            );
        }
    }

    private async _saveValue(): Promise<void> {
        this._isModalSubmitting$.next(true);
        const formValues = this.automaticPaymentFormGroup.value;
        const updateRequest = new UpdateAllPaymentOptInRequest({
            sellerKey: this._userService.getAlternateCompanyKey(),
            optIn:
                formValues.automaticEarlyPaymentSetting !==
                this.automaticEarlyPaymentSettings.DISABLED,
            terms:
                formValues.automaticEarlyPaymentSetting === this.automaticEarlyPaymentSettings.TERMS
                    ? formValues.termsDays
                    : undefined,
            termsActive:
                formValues.automaticEarlyPaymentSetting === this.automaticEarlyPaymentSettings.TERMS
        });
        await this._updateAllPaymentOptInService.executeOnce(updateRequest);
        this.automaticPaymentFormGroup.markAsPristine();
        this.persistedEarlyPaymentSettings = formValues.automaticEarlyPaymentSetting;
        this.persistedEarlyPaymentTerms = formValues.termsDays;
        this._workflowMonitorService.markWorkflowFinished();
        this._modalService.closeModal();

        this._isModalSubmitting$.next(false);
    }

    private async _revertChange(): Promise<void> {
        this._modalService.closeModal();
        await this._reloadSettings();
    }

    private async _reloadSettings(): Promise<void> {
        this.isLoading = true;
        const getOptInRequest = new GetAllPaymentOptInRequest({
            sellerKey: this._userService.getAlternateCompanyKey()
        });
        // tslint:disable-next-line: no-floating-promises
        await this._getAllPaymentOptInService.executeOnce(getOptInRequest).then((response) => {
            const loadedSetting = this._getSetting(response);
            this.persistedEarlyPaymentSettings = loadedSetting;
            this.persistedEarlyPaymentTerms = response.terms;

            this.automaticPaymentFormGroup.reset({
                automaticEarlyPaymentSetting: loadedSetting,
                termsDays: response.terms || null
            });
        });
        this.isLoading = false;
    }

    private _getSetting(
        automaticSetting: GetAllPaymentOptInResponse
    ): AutomaticEarlyPaymentSettings {
        let loadedSetting = automaticSetting.optedIn
            ? this.automaticEarlyPaymentSettings.IMMEDIATELY
            : this.automaticEarlyPaymentSettings.DISABLED;
        if (automaticSetting.termsActive) {
            loadedSetting = this.automaticEarlyPaymentSettings.TERMS;
        }
        return loadedSetting;
    }

    public ngOnDestroy(): void {}
}

export enum AutomaticEarlyPaymentSettings {
    IMMEDIATELY = 'IMMEDIATELY',
    TERMS = 'TERMS',
    DISABLED = 'DISABLED'
}
